import {Grid, Select, MenuItem, FormControl, InputLabel} from '@material-ui/core';
import {SwitchCustom, TextBox, NumberFormatCustom} from '../../../Core/UI';
import React, {useEffect, useMemo} from 'react';
import {Prequal} from '../../../Core/mobx/ParameterStore/Prequal';
import {Parameter} from '../../../Core/mobx/ParameterStore/ParameterStore';
import {observer} from 'mobx-react-lite';
import {CategoryCard} from '../CategoryCard';
import {Approval} from '../../../Core/mobx/ParameterStore/Approval';
import {SelectInputElement} from '../../PreQualification/Components/SelectInputElement';
import {fieldOptions} from '../../../Core/mobx/ParameterStore/ApprovalXref';
import ProductSelector from './ProductSelector';
import RootStore from '../../../Core/mobx/RootStore';
import {PIVOT_REASONS, PivotReasonOption} from '../../../Core/constants/pivotReasons';

type PolicyInformationProps = {
    valueChanged: (p?: Parameter, value?: any) => void;
    parameters: Approval;
    setCategory: (entry: string) => void;
    index: number;
};

const PolicyInformation = observer(({valueChanged, parameters, setCategory, index}: PolicyInformationProps) => {
    const required = (value?: string) => (
        <span>
            {value && value} <span style={{color: 'red'}}>*</span>
        </span>
    );

    const {prequal, qualifiedProducts, recommendedProductsResponse} = RootStore.agentPortalStore.parameterStore;

    // Get all products to map between codes and names
    const allProducts = useMemo(() => {
        return (
            recommendedProductsResponse?.allProducts || recommendedProductsResponse?.products || qualifiedProducts || []
        );
    }, [recommendedProductsResponse, qualifiedProducts]);

    // Get the recommended product code directly from the approval store (copied from prequal)
    const recommendedProductCode = useMemo(() => {
        return parameters.recommendedProductCode?.value || '';
    }, [parameters.recommendedProductCode?.value]);

    // Get pivot reason options based on the recommended product (what they're pivoting FROM)
    const currentPivotOptions = useMemo(() => {
        const recommended = parameters.recommendedProductCode?.value || '';
        const options = PIVOT_REASONS[recommended] || PIVOT_REASONS['default'];
        return options;
    }, [parameters.recommendedProductCode?.value]);

    // Check if current selection is not the recommended product
    const showPivotReason = useMemo(() => {
        const currentSelection = parameters.firstCarrierQuoted?.value;
        const recommended = parameters.recommendedProductCode?.value;
        const shouldShow = currentSelection && recommended && currentSelection !== recommended;
        console.log('PolicyInformation showPivotReason:', {
            currentSelection,
            recommended,
            shouldShow,
            equal: currentSelection === recommended,
        });
        return shouldShow;
    }, [parameters.firstCarrierQuoted?.value, parameters.recommendedProductCode?.value]);

    return (
        <CategoryCard title={'Policy Information'} setCategory={setCategory} index={index}>
            <Grid item xs={4}>
                <ProductSelector
                    param={{
                        label: 'First Carrier Quoted',
                        name: 'firstCarrierQuoted',
                        value: parameters.firstCarrierQuoted?.value,
                        setValue: (v: any) => parameters.firstCarrierQuoted?.setValue?.(v),
                    }}
                    required={required('')}
                    valueChanged={valueChanged}
                />
            </Grid>
            <Grid item xs={4}>
                <SelectInputElement
                    param={{
                        label: 'First Product Type Quoted',
                        name: 'firstProductType',
                        options: fieldOptions.firstProductType.options,
                        ...parameters.firstProductType,
                    }}
                    required={required('')}
                    valueChanged={valueChanged}
                />
            </Grid>
            <Grid item xs={4}>
                <TextBox
                    id="firstCoverageAmount"
                    label={required('First Coverage Amount Quoted')}
                    value={parameters.firstCoverageAmount?.value}
                    onChange={(value) => valueChanged(parameters.firstCoverageAmount, value)}
                    InputProps={{
                        inputComponent: NumberFormatCustom,
                    }}
                />
            </Grid>
            <Grid item xs={4}>
                <TextBox
                    id="firstMonthlyPremium"
                    label={required('1st Carrier Mo. Tgt. Prem.')}
                    value={parameters.firstMonthlyPremium?.value}
                    onChange={(value) => valueChanged(parameters.firstMonthlyPremium, value)}
                    InputProps={{
                        inputComponent: NumberFormatCustom,
                    }}
                />
            </Grid>
            <Grid item xs={4}>
                <SelectInputElement
                    param={{
                        label: 'First Rate Class Quoted',
                        name: 'firstHealthRating',
                        options: fieldOptions.firstHealthRating.options,
                        ...parameters.firstHealthRating,
                    }}
                    required={required('')}
                    valueChanged={valueChanged}
                />
            </Grid>
            <Grid item xs={4}>
                <TextBox
                    id="firstFreeResponse"
                    label={'First Reason'}
                    value={parameters.firstFreeResponse?.value}
                    onChange={(value) => valueChanged(parameters.firstFreeResponse, value)}
                />
            </Grid>
            {showPivotReason && (
                <>
                    <Grid item xs={4}>
                        <FormControl fullWidth>
                            <InputLabel shrink>
                                Reason For Not Choosing The Recommended Product <span style={{color: 'red'}}>*</span>
                            </InputLabel>
                            <Select
                                value={parameters.preapprovalPivotReason?.value || ''}
                                onChange={(e) => {
                                    const newValue = e.target.value as string;
                                    valueChanged(parameters.preapprovalPivotReason, newValue);
                                    // Clear the other reason if switching to a reason that doesn't need it
                                    const selectedOption = currentPivotOptions.find((opt) => opt.text === newValue);
                                    if (
                                        !selectedOption?.requiresExplanation &&
                                        parameters.preapprovalPivotOtherReason
                                    ) {
                                        valueChanged(parameters.preapprovalPivotOtherReason, '');
                                    }
                                }}
                                displayEmpty
                            >
                                <MenuItem value="">
                                    <em>Select a reason</em>
                                </MenuItem>
                                {currentPivotOptions.map((option) => (
                                    <MenuItem key={option.text} value={option.text}>
                                        {option.text}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    {currentPivotOptions.find((opt) => opt.text === parameters.preapprovalPivotReason?.value)
                        ?.requiresExplanation && (
                        <Grid item xs={8}>
                            <TextBox
                                id="preapprovalPivotOtherReason"
                                label={required('Please explain')}
                                value={parameters.preapprovalPivotOtherReason?.value}
                                onChange={(value) => valueChanged(parameters.preapprovalPivotOtherReason, value)}
                            />
                        </Grid>
                    )}
                </>
            )}
            <Grid item xs={4}>
                <ProductSelector
                    param={{
                        label: 'Second Carrier Quoted',
                        name: 'secondCarrierQuoted',
                        value: parameters.secondCarrierQuoted?.value,
                        setValue: (v: any) => parameters.secondCarrierQuoted?.setValue?.(v),
                    }}
                    required={''}
                    valueChanged={valueChanged}
                />
            </Grid>
            <Grid item xs={4}>
                <SelectInputElement
                    param={{
                        label: 'Second Product Type Quoted',
                        name: 'secondProductType',
                        options: fieldOptions.secondProductType.options,
                        ...parameters.secondProductType,
                    }}
                    required={''}
                    valueChanged={valueChanged}
                />
            </Grid>
            <Grid item xs={4}>
                <TextBox
                    id="secondCoverageAmount"
                    label={'Second Coverage Amount Quoted'}
                    value={parameters.secondCoverageAmount?.value}
                    onChange={(value) => valueChanged(parameters.secondCoverageAmount, value)}
                    InputProps={{
                        inputComponent: NumberFormatCustom,
                    }}
                />
            </Grid>
            <Grid item xs={4}>
                <TextBox
                    id="secondMonthlyPremium"
                    label={'2nd Carrier Mo. Tgt. Prem.'}
                    value={parameters.secondMonthlyPremium?.value}
                    onChange={(value) => valueChanged(parameters.secondMonthlyPremium, value)}
                    InputProps={{
                        inputComponent: NumberFormatCustom,
                    }}
                />
            </Grid>
            <Grid item xs={4}>
                <SelectInputElement
                    param={{
                        label: 'Second Rate Class Quoted',
                        name: 'secondHealthRating',
                        options: fieldOptions.secondHealthRating.options,
                        ...parameters.secondHealthRating,
                    }}
                    required={''}
                    valueChanged={valueChanged}
                />
            </Grid>
            <Grid item xs={4}>
                <TextBox
                    id="secondFreeResponse"
                    label={'Second Reason'}
                    value={parameters.secondFreeResponse?.value}
                    onChange={(value) => valueChanged(parameters.secondFreeResponse, value)}
                />
            </Grid>
            <Grid item xs={4}>
                <ProductSelector
                    param={{
                        label: 'Reapply Carrier Quoted',
                        name: 'reapplyCarrierQuoted',
                        value: parameters.reapplyCarrierQuoted?.value,
                        setValue: (v: any) => parameters.reapplyCarrierQuoted?.setValue?.(v),
                    }}
                    required={''}
                    valueChanged={valueChanged}
                />
            </Grid>
            <Grid item xs={4}>
                <SelectInputElement
                    param={{
                        label: 'Reapply Product Type Quoted',
                        name: 'reapplyProductType',
                        options: fieldOptions.reapplyProductType.options,
                        ...parameters.reapplyProductType,
                    }}
                    required={''}
                    valueChanged={valueChanged}
                />
            </Grid>
            <Grid item xs={4}>
                <TextBox
                    id="reapplyCoverageAmount"
                    label={'Reapply Coverage Amount Quoted'}
                    value={parameters.reapplyCoverageAmount?.value}
                    onChange={(value) => valueChanged(parameters.reapplyCoverageAmount, value)}
                    InputProps={{
                        inputComponent: NumberFormatCustom,
                    }}
                />
            </Grid>
            <Grid item xs={4}>
                <TextBox
                    id="reapplyMonthlyPremium"
                    label={'Reapply Mo. Target Premium Quoted'}
                    value={parameters.reapplyMonthlyPremium?.value}
                    onChange={(value) => valueChanged(parameters.reapplyMonthlyPremium, value)}
                    InputProps={{
                        inputComponent: NumberFormatCustom,
                    }}
                />
            </Grid>
            <Grid item xs={4}>
                <SelectInputElement
                    param={{
                        label: 'Reapply Rate Class Quoted',
                        name: 'reapplyHealthRating',
                        options: fieldOptions.reapplyHealthRating.options,
                        ...parameters.reapplyHealthRating,
                    }}
                    required={''}
                    valueChanged={valueChanged}
                />
            </Grid>
            {parameters.state.value === 'NY' && (
                <Grid item xs={4}>
                    <SwitchCustom
                        param={{
                            name: 'nyIllustrationSigned',
                            label: 'NY Illustration Signed',
                            ...parameters.nyIllustrationSigned,
                        }}
                        required={required('')}
                        valueChanged={valueChanged}
                    />
                </Grid>
            )}
        </CategoryCard>
    );
});

export default PolicyInformation;
